import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteCsvFile,
  getCsvFile,
  getCsvFiles,
  getCsvFileVersions,
  updateCsvFileVersionStatus,
  uploadCsvFile,
} from './csvFilesApi';
import type {
  CsvFilesFilters,
  CsvFilesResponse,
  CsvFileValidationStatus,
  CsvFileVersion,
  CsvFileVersionsResponse,
} from '@/types/csvFiles';
import { toast } from '@/utils/toast';

// Query keys for React Query
export const CSV_FILES_QUERY_KEYS = {
  csvFiles: (filters?: CsvFilesFilters) => ['csvFiles', filters],
  csvFile: (id: number) => ['csvFile', id],
  csvFileVersions: (csvFileId: number) => ['csvFileVersions', csvFileId],
} as const;

// Hook to get CSV files with pagination and filtering
export function useCsvFiles(filters?: CsvFilesFilters, enabled = true) {
  return useQuery<CsvFilesResponse>({
    queryKey: CSV_FILES_QUERY_KEYS.csvFiles(filters),
    queryFn: () => getCsvFiles(filters),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in React Query v5)
  });
}

// Hook to get single CSV file
export function useCsvFile(id: number, enabled = true) {
  return useQuery({
    queryKey: CSV_FILES_QUERY_KEYS.csvFile(id),
    queryFn: () => getCsvFile(id),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to get CSV file versions
export function useCsvFileVersions(csvFileId: number, enabled = true) {
  const isCsvFileValidationStatus = (
    value: unknown
  ): value is CsvFileValidationStatus =>
    value === 'pending' || value === 'approved' || value === 'rejected';

  return useQuery<CsvFileVersionsResponse>({
    queryKey: CSV_FILES_QUERY_KEYS.csvFileVersions(csvFileId),
    queryFn: async () => {
      const response = await getCsvFileVersions(csvFileId);

      const versionsWithData: CsvFileVersion[] = response.data.map(version => {
        const v = version as unknown as Record<string, unknown>;
        const backendStatus = v.status ?? v.validation_status;
        const orderIndex = v.order_index ?? v.version_number;

        return {
          ...version,
          csv_file_id: csvFileId,
          validation_status: isCsvFileValidationStatus(backendStatus)
            ? backendStatus
            : 'pending',
          version_number:
            typeof orderIndex === 'number'
              ? orderIndex
              : typeof orderIndex === 'string'
                ? Number(orderIndex)
                : 0,
        };
      });

      return { data: versionsWithData };
    },
    enabled: enabled && !!csvFileId,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to upload CSV file
export function useUploadCsvFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadCsvFile,
    onSuccess: (data, file) => {
      // Invalidate and refetch CSV files list
      queryClient.invalidateQueries({
        queryKey: ['csvFiles'],
      });

      // Show success toast
      toast.success(`${file.name} uploaded successfully`, {
        description: 'CSV file has been uploaded and is ready for use',
      });
    },
    onError: (error: Error, file) => {
      // Show error toast with details
      toast.error(`Failed to upload ${file.name}`, {
        description:
          error.message || 'Please check the file format and try again',
      });
    },
  });
}

// Hook to delete CSV file
export function useDeleteCsvFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCsvFile,
    onSuccess: (_, fileId) => {
      // Invalidate and refetch CSV files list
      queryClient.invalidateQueries({
        queryKey: ['csvFiles'],
      });

      // Also invalidate specific file and its versions
      queryClient.invalidateQueries({
        queryKey: CSV_FILES_QUERY_KEYS.csvFile(fileId),
      });
      queryClient.invalidateQueries({
        queryKey: CSV_FILES_QUERY_KEYS.csvFileVersions(fileId),
      });

      // Show success toast
      toast.success('CSV file deleted successfully');
    },
    onError: (error: Error) => {
      // Show error toast
      toast.error('Failed to delete CSV file', {
        description: error.message || 'Please try again',
      });
    },
  });
}

// Hook to upload multiple CSV files with progress tracking
export function useUploadMultipleCsvFiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (files: File[]) => {
      const results = [];
      const errors = [];

      for (const file of files) {
        try {
          const result = await uploadCsvFile(file);
          results.push({ file, result, success: true });
        } catch (error) {
          errors.push({ file, error, success: false });
        }
      }

      return { results, errors, totalFiles: files.length };
    },
    onSuccess: ({ results, errors, totalFiles }) => {
      // Invalidate CSV files list
      queryClient.invalidateQueries({
        queryKey: ['csvFiles'],
      });

      // Show appropriate toast messages
      if (errors.length === 0) {
        // All successful
        toast.success(`${totalFiles} CSV file(s) uploaded successfully`, {
          description: 'File(s) are ready for use',
        });
      } else if (results.length === 0) {
        // All failed
        toast.error(`Failed to upload all ${totalFiles} CSV files`, {
          description: 'Please check file formats and try again',
        });
      } else {
        // Partial success
        toast.warning(`${results.length} of ${totalFiles} files uploaded`, {
          description: `${errors.length} files failed to upload`,
        });
      }
    },
    onError: (error: Error) => {
      toast.error('Upload failed', {
        description: error.message || 'Please try again',
      });
    },
  });
}

// Hook to update CSV file version status
export function useUpdateCsvFileVersionStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      csvFileId,
      versionId,
      status,
    }: {
      csvFileId: number;
      versionId: number;
      status: CsvFileValidationStatus;
      versionFilename: string;
    }) => updateCsvFileVersionStatus(csvFileId, versionId, status),
    onSuccess: (_, variables) => {
      const { csvFileId, status, versionFilename } = variables;
      const statusLabels: Record<CsvFileValidationStatus, string> = {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
      };

      // Invalidate and refetch related queries
      queryClient.invalidateQueries({
        queryKey: ['csvFiles'],
      });
      queryClient.invalidateQueries({
        queryKey: CSV_FILES_QUERY_KEYS.csvFile(csvFileId),
      });
      queryClient.invalidateQueries({
        queryKey: CSV_FILES_QUERY_KEYS.csvFileVersions(csvFileId),
      });

      // Show success toast (only once at API level)
      toast.success('Status updated', {
        description: `${versionFilename} status changed to ${statusLabels[status]}`,
      });
    },
    onError: (error: Error) => {
      // Show error toast
      toast.error('Failed to update status', {
        description:
          error instanceof Error
            ? error.message
            : 'Please try again later',
      });
    },
  });
}
